import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOptionsDashboard } from '../../services/api';
import StandardLoader from '../StandardLoader';
import TickerDashboardGrid from './TickerDashboardGrid';
import { useLastClickedTicker } from '../../contexts/LastClickedTickerContext';
import { TrendingUp } from 'lucide-react';

/**
 * OptionsDashboard - Displays the options template dashboard for a specific contract
 *
 * URL Pattern: /options-dashboard/:ticker/:optionType/:strike/:expiration
 *
 * This component:
 * 1. Loads the user's options template dashboard
 * 2. Applies the contract details to dynamic options widgets
 * 3. Updates the LastClickedTicker context for dynamic widget behavior
 */
function OptionsDashboard() {
  const { ticker, optionType, strike, expiration } = useParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update context when this page loads
  const { setLastClickedTicker, setLastClickedContract } = useLastClickedTicker();

  useEffect(() => {
    // Update context so dynamic widgets across the app react
    setLastClickedTicker(ticker);
    setLastClickedContract({
      option_type: optionType,
      strike: parseFloat(strike),
      expiration: expiration
    });

    loadOptionsDashboard();
  }, [ticker, optionType, strike, expiration]);

  const loadOptionsDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getOptionsDashboard(ticker, optionType, strike, expiration);
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error loading options dashboard:', err);
      setError(`Failed to load dashboard for ${ticker} $${strike} ${optionType?.toUpperCase()} ${expiration}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <StandardLoader />;

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-500 mb-4">{error}</div>
        <Link to="/" className="text-primary hover:underline">
          ← Back to Screener
        </Link>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-8 text-center">
        <div className="text-gray-400 mb-4">No dashboard data available</div>
        <Link to="/" className="text-primary hover:underline">
          ← Back to Screener
        </Link>
      </div>
    );
  }

  // Format contract description for display
  const contractDesc = `${ticker} $${strike} ${optionType?.toUpperCase()} ${expiration}`;
  const isCall = optionType?.toLowerCase() === 'call';

  return (
    <div className="options-dashboard">
      <div className="bg-background border-b border-muted p-4 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <TrendingUp className={`w-6 h-6 ${isCall ? 'text-green-400' : 'text-red-400'}`} />
              <h1 className="text-2xl font-bold text-primary">{contractDesc}</h1>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Options Premium Analysis • {dashboardData.widgets?.length || 0} widgets
              {dashboardData.widgets?.some(w => w.use_dynamic_ticker) && (
                <span className="ml-2 text-blue-400">
                  • {dashboardData.widgets.filter(w => w.use_dynamic_ticker).length} dynamic
                </span>
              )}
            </p>
          </div>

          <div className="flex gap-3">
            {/* Link to underlying stock dashboard */}
            <Link
              to={`/dashboard/${ticker}`}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition text-sm flex items-center gap-2"
            >
              📈 View {ticker} Stock
            </Link>

            <Link
              to="/dashboard-builder"
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded transition text-sm flex items-center gap-2"
            >
              ⚙️ Edit Template
            </Link>

            <Link
              to="/"
              className="bg-muted hover:bg-muted/80 text-text px-4 py-2 rounded transition text-sm"
            >
              ← Back to Screener
            </Link>

            <Link
              to="/watchlist"
              className="bg-muted hover:bg-muted/80 text-text px-4 py-2 rounded transition text-sm"
            >
              Watchlist
            </Link>
          </div>
        </div>
      </div>

      {/* Reuse TickerDashboardGrid - it handles both stock and options widgets */}
      <TickerDashboardGrid dashboardData={dashboardData} ticker={ticker} />
    </div>
  );
}

export default OptionsDashboard;
